import { FC, useState, Fragment, memo } from 'react';
// Hooks
import UseToggle from 'app/hooks/useToggle';
// Models
import IInsuranceCase from 'app/models/Case';
import { IWorkflow } from 'app/store/AutomatedWorkflows/AutomatedWorkflows.models';
// store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { executeInstantWorkflow } from 'app/store/AutomatedWorkflowsExecutions/AutomatedWorkflowsExecutions.async';
// Selectors
import { selectCaseAiChatEnabled, selectInsuranceCase } from 'app/store/Cases/Cases.selectors';
// Mui
import { Box, Paper, Typography, Tooltip, CircularProgress } from '@mui/material';
// Icons
import {
  PlayCircleFilledWhiteOutlined as PlayCircleFilledWhiteOutlinedIcon,
} from '@mui/icons-material';
// Components
import { IconButton } from 'app/components/Mui';
import Icon from 'app/components/Icon';
import WorkflowDialog from 'app/dialogs/WorkflowDialog';
// i18next
import { useTranslation } from 'react-i18next';

type Props = {
  workflow: IWorkflow;
}

const WorkflowItem:FC<Props> = ({ workflow }) => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // Selectors
  const caseAiChatEnabled = useAppSelector(selectCaseAiChatEnabled);
  const insuranceCase = useAppSelector(selectInsuranceCase) as IInsuranceCase;

  const [ isLoading, setIsLoading] = useState(false);

  const { open, toggle } = UseToggle();

  const handleRunWorkflow = (documentIds:number[] = []) => async () => {
    setIsLoading(true);
    try {
      const data:any = {
        caseId: insuranceCase.id,
        workflowId: workflow.id,
      }
      if (documentIds.length) data['parameters'] = { documentIds };
      await dispatch(executeInstantWorkflow(data));
      if (open) toggle();
    } catch(error){
      console.error(error)
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Fragment key={`workflow item ${workflow.id}`}>
      <Paper
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3, py: 1
        }}
        variant="outlined"
      >
        <Box sx={{ flexGrow: 1, pr: 2 }}>
          <Typography variant="subtitle1" fontWeight={500}>{workflow.name}</Typography>
        </Box>
        <Tooltip title={caseAiChatEnabled ? t('pages.workflows.runButton') : t('pages.workflows.aiChatDisabledHint')}>
          <span>
            <IconButton
              name={`Run workflow ${workflow.id}`}
              aria-label="Run workflow button"
              onClick={handleRunWorkflow()}
              disabled={!caseAiChatEnabled || isLoading}
              size="small"
            >{isLoading ? <CircularProgress size={24} thickness={6} /> : <PlayCircleFilledWhiteOutlinedIcon />}</IconButton>
          </span>
        </Tooltip>
        <Tooltip title={caseAiChatEnabled ? t('pages.workflows.customRunButton') : t('pages.workflows.aiChatDisabledHint')}>
          <span>
            <IconButton
              name={`Custom run workflow ${workflow.id}`}
              aria-label="Custom run workflow button"
              onClick={toggle}
              disabled={!caseAiChatEnabled || isLoading}
              size="small"
              >{isLoading ? <CircularProgress size={24} thickness={6} /> : <Icon icon="svgviewer-output-2" />}</IconButton>
          </span>
        </Tooltip>
      </Paper>

      {open ? (
        <WorkflowDialog
          open={open}
          onClose={toggle}
          onSubmit={handleRunWorkflow}
        />
      ) : null}
    </Fragment>
  )
}

export default memo(WorkflowItem);
